import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';

interface UrlHighlighterOptions {
  enabled?: Ref<boolean> | boolean;
  regex?: RegExp;
}

export interface UrlHighlighterApi {
  textareaRef: Ref<HTMLTextAreaElement | null>;
  overlayRef: Ref<HTMLDivElement | null>;
  highlightedHtml: ComputedRef<string>;
  onTextareaScroll: () => void;
}

const DEFAULT_REGEX = /\b((?:https?:\/\/|www\.)[^\s<]+)/gi;

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

const escapeHtml = (input: string): string => {
  return input.replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char] ?? char);
};

export function useUrlHighlighter(
  model: Ref<string | undefined>,
  options: UrlHighlighterOptions = {}
): UrlHighlighterApi {
  const textareaRef = ref<HTMLTextAreaElement | null>(null);
  const overlayRef = ref<HTMLDivElement | null>(null);

  const isEnabled = computed(() => {
    const v = options.enabled;
    return typeof v === 'boolean' ? v : (v?.value ?? true);
  });

  const regex = computed(() => options.regex ?? DEFAULT_REGEX);

  const highlightedHtml = computed((): string => {
    const value = model.value ?? '';
    if (!isEnabled.value || value.length === 0) return '';
    return escapeHtml(value).replace(regex.value, (match) => {
      return `<span class="underline decoration-1">${escapeHtml(match)}</span>`;
    });
  });

  const onTextareaScroll = (): void => {
    if (!textareaRef.value || !overlayRef.value) return;
    overlayRef.value.scrollTop = textareaRef.value.scrollTop;
    overlayRef.value.scrollLeft = textareaRef.value.scrollLeft;
  };

  watch(model, () => {
    onTextareaScroll();
  });

  return {
    textareaRef,
    overlayRef,
    highlightedHtml,
    onTextareaScroll
  };
}
